/**
 * History Page Component
 * Renders an interactive historical timeline documenting the origin, mining heritage,
 * colliery decline resilience, and modern community transformation of Clipstone Miners Welfare.
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../../Components/Layout/Navbar.css';
import './History.css';

// Historical timeline milestones data
const historyData = [
  {
    heading: "Introduction to the Clipstone Miners Welfare",
    date: "Early 20th century",
    info: "Clipstone Miners Welfare stands as a cornerstone of the Clipstone community, its legacy interwoven with the vibrant tapestry of Nottinghamshire's coal mining heritage. Established to serve and support local miners and their families, the Welfare has evolved over the decades, adapting to changing times while preserving its core values of community, resilience, and togetherness. Today, it remains a beacon of social and cultural life, welcoming generations of residents through its doors."
  },
  {
    heading: "Origins and Early Years",
    date: "1953",
    info: "The Welfare was founded during the early 20th century, at the height of coal mining in Clipstone. As miners toiled underground, the Welfare provided much-needed relief above ground—a place to unwind, share stories, and build friendships. Facilities included sports pitches, halls for gatherings, and spaces for educational and recreational activities. It quickly became the heart of village life, organizing dances, sporting leagues, and holiday celebrations that brought the community closer together."
  },
  {
    heading: "Community Resilience During Mining Decline",
    date: "1972",
    info: "When the colliery faced closure, Clipstone endured economic and social upheaval. The Welfare, however, adapted with determination, opening its doors to a broader community and diversifying its activities. No longer just a miners' refuge, it became a lifeline for local families, offering support networks, skill-building workshops, and events to foster unity. The ability to pivot and serve new generations is a testament to the spirit and resilience of Clipstone's people."
  },
  {
    heading: "Modern Developments and Recent Times",
    date: "1986",
    info: "In recent years, Clipstone Miners Welfare has continued to grow as a dynamic hub. Upgrades to facilities have enabled it to host a wider range of events—from sports tournaments and charity fundraisers to concerts and community fairs. The Welfare actively engages with local schools and organizations, promoting health, education, and wellbeing. Its calendar is filled with activities for all ages, ensuring that it remains a lively and inclusive space at the heart of the village."
  },
  {
    heading: "Working Towards Net Zero",
    date: "2000",
    info: "Mindful of its responsibility to the environment, the Welfare is committed to playing a part in the journey towards net zero. Recent initiatives include the installation of energy-efficient lighting, the introduction of recycling programs, and the exploration of renewable energy sources such as solar panels and batteries. Sustainability workshops and green community projects have been launched to encourage everyone to get involved in reducing their environmental impact. The Welfare's forward-thinking approach sets an inspiring example for others in the region."
  },
  {
    heading: "Legacy and community Engagement",
    date: "2003",
    info: "The legacy of Clipstone Miners Welfare is measured not just in its buildings or facilities, but in the sense of belonging it continues to foster. Many families can trace their roots through the Welfare's history, from miners who built the foundations to today's children playing sports on its fields. Annual reunions, heritage projects, and storytelling sessions keep local traditions alive. The Welfare's enduring presence is a living tribute to the solidarity and pride of Clipstone."
  },
  {
    heading: "Trust Reorganisation",
    date: "2006",
    info: "To reflect a modern focus on the entire village rather than just miners, the Charity Commission approved a scheme to rename the organisation the Clipstone Miners Welfare Community Trust."
  },
  {
    heading: "Conclusion",
    date: "2020",
    info: "From its origins as the social heart of a mining village to its evolving role in a modern, sustainable community, Clipstone Miners Welfare exemplifies adaptability and unity. Its journey is one of perseverance, innovation, and hope for the future—a vision of a thriving, inclusive space where every resident can feel at home. As the Welfare looks ahead, it remains dedicated to supporting, inspiring, and connecting the people of Clipstone for generations to come."
  },
  {
    heading: "The Future of Clipstone Miners Welfare",
    date: "2027+",
    info: "To help raise online presence for our community trust, we launched a new website to showcase our history, events, and initiatives. This digital platform allows us to reach a wider audience, share our story, and engage with both local residents and visitors interested in the rich heritage of Clipstone, Devolop a new sports faciality and recriation hub at the former colliery  site and complete refurbishment of the institute building and achive net zero."
  },
];

const TimelineItem = ({ item, index }) => {
  const itemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentItem = itemRef.current;
    if (currentItem) {
      observer.observe(currentItem);
    }

    return () => {
      if (currentItem) {
        observer.unobserve(currentItem);
      }
    };
  }, []);

  const sideClass = index % 2 === 0 ? 'left' : 'right';

  return (
    <div className={`timeline-item ${sideClass}`} ref={itemRef}>
      <div className="timeline-content">
        <h2>{item.heading}</h2>
        {item.date && <div className="timeline-date">{item.date}</div>}
        <p>{item.info}</p>
      </div>
    </div>
  );
};

const History = () => {
  return (
    <div className="history-page">
      <section className="history-header">
        <h1>Our History</h1>
        <p className="history-subtitle">
          From the deep coal seams of the 20th century to a vibrant community hub today, 
          explore the journey of the Clipstone Miners Welfare Community Trust.
        </p>
      </section>

      <div className="history-container">
        {historyData.map((item, index) => (
          <TimelineItem key={index} item={item} index={index} />
        ))}
      </div>

      <section style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Link to="/trust" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Back to Trust Page
        </Link>
      </section>
    </div>
  );
};

export default History;
